import { toRaw, isProxy } from '@vue/reactivity'
import { useMiscStore } from '~/stores/misc'
import { useUserStore } from '~/stores/user'
import { useTranslationStore } from '~/stores/translation'

export function until(conditionFunction: Function) {
  const poll = (resolve: Function) => {
    if (conditionFunction()) resolve()
    else setTimeout((_: any) => poll(resolve), 10)
  }
  return new Promise(poll)
}

export function empty_promise() {
  return new Promise((resolve, _) => {
    resolve(null)
  })
}

export function objects_equal(x: any, y: any): boolean {
  const ok = Object.keys,
    tx = typeof x,
    ty = typeof y
  return x && y && tx === 'object' && tx === ty
    ? ok(x).length === ok(y).length && ok(x).every((key) => objects_equal(x[key], y[key]))
    : x === y
}

export function debounce(func: Function, wait: number, immediate = false) {
  let timeout: any
  return function () {
    // @ts-expect-error - need `this` and `any` to make this work
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context: any = this
    // eslint-disable-next-line prefer-rest-params
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}

export function store_info(store_number: number | string) {
  const miscStore = useMiscStore()
  store_number = typeof store_number === 'string' ? parseInt(store_number) : store_number
  if (miscStore.stores[store_number]) {
    return miscStore.stores[store_number]
  } else {
    miscStore.getStoreInfo(store_number).then((_) => {})
    return {}
  }
}

export function title_case(value: string) {
  if (value === null || value === undefined) {
    return null
  }
  let out = value.substring(0, 1).toUpperCase() + value.substring(1).toLowerCase()
  for (const c of [
    [' ', /\s/g],
    ['/', /\//g],
    ['-', /-/g],
    ['_', /_/g],
  ] as Array<[string, RegExp]>) {
    if (out.includes(c[0])) {
      let temp = ''
      for (const word of out.split(c[1])) {
        temp += word.substring(0, 1).toUpperCase() + word.substring(1) + c[0]
      }
      out = temp.substring(0, temp.length - 1)
    }
  }
  return out
}

export function currency(value: string | number, locale: string | null = null, _default = null) {
  if (typeof value === 'string' && !isNaN(parseFloat(value))) {
    value = parseFloat(value)
  }
  if (value === null || value === undefined) {
    return _default
  }
  if (locale === null) {
    locale = useUserStore().locale
  }
  return value
    .toLocaleString(locale || 'en-US', {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'symbol',
    })
    .replace(' US', '')
    .replace('CA', '')
}

interface PercentOptions {
  decimals?: number
  as_string?: boolean
}

export function percent(numerator: number, divisor: number, options: PercentOptions) {
  options = options === undefined ? {} : options
  const decimals = options.decimals !== undefined ? options.decimals : 1
  const as_string = options.as_string !== undefined ? options.as_string : true
  const scaler = 10 ** decimals
  const result = Math.round((numerator / divisor) * 100 * scaler) / scaler
  return as_string ? `${result}%` : result
}

export function dollars(value: string | number, locale: string | null = null, _default = null) {
  if (typeof value === 'string' && !isNaN(parseFloat(value))) {
    value = parseFloat(value)
  }
  if (value === null || value === undefined) {
    return _default
  }
  if (locale === null) {
    locale = useUserStore().locale
  }
  return value
    .toLocaleString(locale || 'en-US', {
      style: 'currency',
      currency: 'CAD',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(' US', '')
    .replace('CA', '')
}

export function fmt_number(value: string | number, locale: string | null = null, _default = null) {
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  if (value === null || value === undefined || isNaN(value)) {
    return _default
  }
  if (locale === null) {
    locale = useUserStore().locale
  }
  return new Intl.NumberFormat(locale || 'en-US').format(value)
}

export function deepcopy(obj: object) {
  if (isProxy(obj)) {
    return structuredClone(toRaw(obj))
  }
  return structuredClone(obj)
}

interface DateOptions {
  locale?: string
  default?: any
  year?: number
  dow?: string
}

function _parse_date(value: Date | string | number) {
  if (typeof value === 'string') {
    // Assume non UTC timezones are EST
    if (value.indexOf('Z') < 0) {
      // if it's a date, e.g. 'YYYY-MM-DD' skip string parsing to avoid time zone issues
      const m = value.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/)
      if (m) {
        return new Date(parseInt(m[1]!), parseInt(m[2]!) - 1, parseInt(m[3]!))
      }
      return est_to_local(new Date(value)) || new Date(value)
    } else {
      return new Date(value)
    }
  }
  return value instanceof Date ? value : new Date(value)
}

export function date_string(
  value: Date | string | number | null | undefined,
  options?: DateOptions,
) {
  options = options === undefined ? {} : options
  let locale = options.locale !== undefined ? options.locale : null
  const _default = options.default !== undefined ? options.default : null
  const year = options.year !== undefined ? options.year : true
  const dow = options.dow !== undefined ? options.dow : true

  if (value === null || value === undefined || value === '') {
    return _default
  }
  const dt = _parse_date(value)

  if (locale === null) {
    locale = useUserStore().locale
  }

  interface DateFormat {
    month: string
    day: string
    dow?: string
    year?: string
  }

  const fmt: DateFormat = {
    month: 'short',
    day: 'numeric',
  }
  if (dow) {
    fmt.dow = 'short'
  }
  if (year) {
    fmt.year = 'numeric'
  }
  return dt.toLocaleDateString(locale || 'en-US', fmt as object)
}

export function to_html_date(value: Date | string | number | null | undefined) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const dt = _parse_date(value)
  let month = '' + (dt.getMonth() + 1)
  let day = '' + dt.getDate()
  const year = dt.getFullYear()
  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day
  return [year, month, day].join('-')
}

export function est_to_local(value: Date | string | number | null | undefined) {
  // Calculate the difference between the browsers local time and toronto time
  if (value === null || value === undefined || value === '') {
    return null
  }
  const dt = _parse_date(value)
  const local_str = dt.toLocaleString('en-US')
  const est_str = dt.toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const local_diff = new Date(est_str).getTime() - new Date(local_str).getTime()

  // apply that to an input string
  const out = new Date(value)
  out.setTime(out.getTime() - local_diff)
  return out
}

export function time_string(
  value: Date | string | number | null | undefined,
  options: object = {},
) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const dt = _parse_date(value)
  const userStore = useUserStore()
  return dt.toLocaleTimeString(userStore.locale || 'en-US', options)
}

export function date_time_string(
  value: Date | string | number | null | undefined,
  date_options?: DateOptions,
  time_options?: object,
) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const dt = _parse_date(value)
  return date_string(dt, date_options) + ' ' + time_string(dt, time_options || {})
}

export function sum(arr: number[]) {
  return arr.reduce((acc, val) => acc + val, 0)
}

export function mean(arr: number[]) {
  return sum(arr) / arr.length
}

export function median(arr: number[]) {
  if (arr.length === 0) {
    return null
  }
  if (arr.length === 1) {
    return arr[0]
  }
  arr = arr.toSorted((a, b) => a - b)
  const half = Math.floor(arr.length / 2)
  if (arr.length % 2) {
    return arr[half]
  }
  return (arr[half - 1]! + arr[half]!) / 2.0
}

export function percentile(arr: number[], pct: number) {
  if (arr.length === 0) {
    return null
  }
  if (arr.length === 1) {
    return arr[0]
  }
  arr = arr.toSorted((a, b) => a - b)
  const pt = Math.floor(arr.length / (1 / pct))
  if (arr.length % 2) {
    return arr[pt]
  }
  return (arr[pt - 1]! + arr[pt]!) / 2.0
}

export function sanitize_filename(value: string) {
  const chars = '\\/:*?"<>|'
  for (let i = 0; i < chars.length; i++) {
    while (value.includes(chars[i]!)) {
      value = value.replace(chars[i]!, '_')
    }
  }
  return value || `file`
}

export function percent_change(current: number, old: number, html = false, as_string: false) {
  current = current || 0
  old = old || 0
  if (!old || old === 0.0 || !current) {
    if (Math.abs(current) > 0) {
      return as_string ? '∞' : html ? '<span>-</span>' : null
    } else {
      return as_string ? '+0%' : html ? '<span>-</span>' : null
    }
  }

  const val = Math.round(((current - old) / old) * 1000) / 10
  if (html || as_string) {
    if (val > 0) {
      return html ? '<span style="color: mediumseagreen">(' + val + '%)</span>' : `${val}%`
    } else if (val < 0) {
      return html ? '<span style="color: red">(' + val + '%)</span>' : `${val}%`
    }
    return html ? `<span>(${val}%)</span>` : `${val}%`
  }
  return val
}

export function enter_key(evt: KeyboardEvent, fun: Function) {
  // Executes the passed function if the Enter key is pressed
  if (evt.keyCode === 13) {
    return fun()
  }
}

export function best_price(arr: Array<number | null | undefined>): number | null {
  let lowest = Infinity
  for (let price of arr) {
    price = price || 0
    if (price > 0 && price < lowest) {
      lowest = price
    }
  }
  return lowest === Infinity ? null : lowest
}

export function month_name(idx: number, zero_indexed = false) {
  const m = [
    null,
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  return m[zero_indexed ? idx + 1 : idx]
}

export function sort_fn(a: any, b: any, dir: 'asc' | 'desc') {
  if (a < b) {
    return dir === 'desc' ? 1 : -1
  }
  if (a > b) {
    return dir === 'desc' ? -1 : 1
  }
  return 0
}

export function shopify_admin_order_url(env: string, order_id: string | number) {
  const suffix = env === 'dev' ? '-dev' : env === 'uat' ? '-uat1' : ''
  return `https://admin.shopify.com/store/sourceforsports-ca${suffix}/orders/${order_id}`
}

export function osfc_env_string(env: string | null | undefined) {
  if (env === 'prod' || !env) {
    return ''
  }
  return `${env}/`
}

export function unit_format(
  input_value: number,
  input_units: string,
  output_units: string,
  decimal_places: number = 1,
) {
  if (input_units === output_units) {
    return Math.round(input_value * 10 ** decimal_places) / 10 ** decimal_places
  }
  if (input_units === 'in' && output_units === 'cm') {
    return Math.round(input_value * 2.54 * 10 ** decimal_places) / 10 ** decimal_places
  }
  if (input_units === 'cm' && output_units === 'in') {
    return Math.round((input_value / 2.54) * 10 ** decimal_places) / 10 ** decimal_places
  }
  if (input_units === 'kg' && output_units === 'lb') {
    return Math.round(input_value * 2.20462 * 10 ** decimal_places) / 10 ** decimal_places
  }
  if (input_units === 'lb' && output_units === 'kg') {
    return Math.round((input_value / 2.20462) * 10 ** decimal_places) / 10 ** decimal_places
  }
  return null
}

export function tr(phrase: string) {
  if (!phrase) {
    return ''
  }
  const userStore = useUserStore()
  const to = userStore.locale || 'en-US'
  if (to === 'en-US') {
    return phrase
  }

  const translationStore = useTranslationStore()
  translationStore.getTranslation(to, phrase).then(() => {})
  return translationStore.translated(to, phrase)
}

export async function tr_async(phrase: string): Promise<string> {
  if (!phrase) {
    return ''
  }
  const to = useUserStore().locale || 'en-US'
  if (to === 'en-US') {
    return phrase
  }
  return await useTranslationStore().getTranslation(to, phrase)
}
